import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Bug, ChevronDown, RefreshCw, Settings, Trash2 } from '../ui/icons';
import {
  PROPERTY_FIELDS,
  FieldInfo,
  ScraperJsConfig,
  TransformConfig,
  ValueMapping,
  JsExtractorPreview,
} from '../../models';
import FieldConfigDialog, {
  FieldConfigInlinePanel,
  formatPreviewValue,
} from './FieldConfigDialog';
import ScraperPreviewPanel from './ScraperPreviewPanel';
import { useFieldConfiguration } from '../../hooks/useFieldConfiguration';
import { usePreviewState } from '../../hooks/usePreviewState';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  Badge,
  Card,
  Dialog,
  Input,
  Select,
  SelectOption,
  Spinner
} from '../ui';

const DEFAULT_FIELD_IDS = [
  'aiTextContent',
  'title',
  'price',
  'priceQualifier',
  'availability',
  'summary',
  'features',
  'photos',
  'floorplans',
  'epcs',
  'virtualTours',
  'latitude',
  'longitude',
  'bedrooms',
  'bathrooms',
  'receptionRooms',
  'propertyType',
];

interface ScraperConfigFieldsProps {
  selectors: Record<
    string,
    {
      selector: string;
      attribute?: string;
      transform?: TransformConfig;
      defaultValue?: any;
      valueMappings?: ValueMapping[];
    }
  >;
  jsConfigs: ScraperJsConfig[];
  onSelectorsChange: (selectors: Record<string, any>) => void;
  onJsConfigsChange: (configs: ScraperJsConfig[]) => void;
  previewValues?: Record<string, any>;
  jsExtractorResults?: JsExtractorPreview[];
  previewHtml?: string | null;
  previewSourceUrl?: string | null;
  previewLoading?: boolean;
  previewError?: string | null;
  previewUrlInput?: string;
  onPreviewUrlInputChange?: (url: string) => void;
  onLoadPreview?: (url: string) => void | Promise<void>;
  previewLoadLoading?: boolean;
  previewLoadError?: string | null;
  onClearPreview?: () => void;
  onPreviewRefresh?: () => void;
  onTestUrl?: () => void;
}

type SelectorConfig = ScraperConfigFieldsProps['selectors'][string];

const ScraperConfigFields: React.FC<ScraperConfigFieldsProps> = React.memo(
  ({
    selectors,
    jsConfigs,
    onSelectorsChange,
    onJsConfigsChange,
    previewValues,
    jsExtractorResults,
    previewHtml,
    previewSourceUrl,
    previewLoading = false,
    previewError = null,
    previewUrlInput = '',
    onPreviewUrlInputChange,
    onLoadPreview,
    previewLoadLoading = false,
    previewLoadError = null,
    onClearPreview,
    onPreviewRefresh,
    onTestUrl,
  }) => {
    const initializedDefaults = useRef(false);
    const [previewAccordionExpanded, setPreviewAccordionExpanded] = useState(
      Boolean(previewSourceUrl)
    );
    const [pendingAssignSelector, setPendingAssignSelector] = useState<
      string | null
    >(null);
    const [assignFieldId, setAssignFieldId] = useState<string | null>(null);

    // Use custom hooks for field configuration
    const {
      fieldMap,
      activeFields,
      availableFields,
      activePreviewFieldId,
      setActivePreviewFieldId,
      inlineFieldId,
      setInlineFieldId,
      fieldConfigDialogId,
      setFieldConfigDialogId,
      manualFieldOrder,
      setManualFieldOrder,
      handleFieldSelect,
      handleSelectorUpdate,
      handleSelectorRemove,
    } = useFieldConfiguration({
      selectors,
      onSelectorsChange,
    });

    // Use custom hook for preview state
    const {
      previewSelectorInput,
      setPreviewSelectorInput,
      previewSelectorAllInput,
      setPreviewSelectorAllInput,
      previewSelectorError,
      setPreviewSelectorError,
      previewSelectorAllError,
      setPreviewSelectorAllError,
      activePreviewField,
      previewSinglePlaceholder,
      previewMultiPlaceholder,
    } = usePreviewState({
      activePreviewFieldId,
      fieldMap,
      selectors,
    });

    const selectableFields = useMemo(
      () => [...PROPERTY_FIELDS].sort((a, b) => a.name.localeCompare(b.name)),
      []
    );

    const handleAddJsConfig = useCallback(() => {
      onJsConfigsChange([
        ...jsConfigs,
        {
          selector: '',
          function: '',
        },
      ]);
    }, [jsConfigs, onJsConfigsChange]);

    const handleJsConfigChange = useCallback(
      (index: number, updates: Partial<ScraperJsConfig>) => {
        const updated = jsConfigs.map((config, idx) =>
          idx === index ? { ...config, ...updates } : config
        );
        onJsConfigsChange(updated);
      },
      [jsConfigs, onJsConfigsChange]
    );

    const handleJsConfigRemove = useCallback(
      (index: number) => {
        const updated = jsConfigs.filter((_, idx) => idx !== index);
        onJsConfigsChange(updated);
      },
      [jsConfigs, onJsConfigsChange]
    );

    useEffect(() => {
      if (previewSourceUrl) {
        setPreviewAccordionExpanded(true);
      }
    }, [previewSourceUrl]);

    // Seed default fields once for convenience
    useEffect(() => {
      if (initializedDefaults.current) {
        return;
      }
      initializedDefaults.current = true;

      const updatedSelectors = { ...selectors };
      let shouldUpdate = false;
      DEFAULT_FIELD_IDS.forEach((fieldId) => {
        if (!updatedSelectors[fieldId]) {
          updatedSelectors[fieldId] = { selector: '' };
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        onSelectorsChange(updatedSelectors);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const assignSelectorToField = useCallback(
      (fieldId: string, selectorValue: string) => {
        const field = fieldMap.get(fieldId);
        if (!field) {
          return false;
        }

        const currentSelector = selectors[fieldId] || {
          selector: '',
        };

        handleSelectorUpdate(fieldId, {
          ...currentSelector,
          selector: selectorValue,
        });

        if (field.multiple) {
          setPreviewSelectorAllInput(selectorValue);
          setPreviewSelectorInput('');
        } else {
          setPreviewSelectorInput(selectorValue);
          setPreviewSelectorAllInput('');
        }

        setActivePreviewFieldId(fieldId);
        setInlineFieldId(fieldId);
        setFieldConfigDialogId(null);
        setManualFieldOrder((prev) => [
          fieldId,
          ...prev.filter((id) => id !== fieldId),
        ]);

        return true;
      },
      [
        fieldMap,
        handleSelectorUpdate,
        selectors,
        setPreviewSelectorAllInput,
        setPreviewSelectorInput,
        setManualFieldOrder,
        setActivePreviewFieldId,
        setInlineFieldId,
        setFieldConfigDialogId,
      ]
    );

    const handleAssignSuggestionRequest = useCallback(
      (selectorValue: string) => {
        setPendingAssignSelector(selectorValue);
        setAssignFieldId(activePreviewFieldId);
      },
      [activePreviewFieldId]
    );

    const handleAssignDialogClose = useCallback(() => {
      setPendingAssignSelector(null);
      setAssignFieldId(null);
    }, []);

    const handleAssignDialogConfirm = useCallback(() => {
      if (!pendingAssignSelector || !assignFieldId) {
        return;
      }

      assignSelectorToField(assignFieldId, pendingAssignSelector);
      setPendingAssignSelector(null);
      setAssignFieldId(null);
    }, [assignFieldId, assignSelectorToField, pendingAssignSelector]);

    const assignSelectedField = assignFieldId
      ? fieldMap.get(assignFieldId) || null
      : null;
    const assignDialogOpen = Boolean(pendingAssignSelector);
    const inlineField = inlineFieldId
      ? fieldMap.get(inlineFieldId) || null
      : null;
    const inlineSelector = inlineField
      ? selectors[inlineField.id] || { selector: '' }
      : null;

    let dialogField: FieldInfo | null = null;
    let dialogSelector: SelectorConfig | null = null;
    let inlineFieldConfigContent: React.ReactNode = null;

    if (inlineField && inlineSelector) {
      const currentField = inlineField;
      const currentSelector = inlineSelector;
      inlineFieldConfigContent = (
        <FieldConfigInlinePanel
          field={currentField}
          selector={currentSelector}
          onUpdate={(updatedSelector) =>
            handleSelectorUpdate(currentField.id, updatedSelector)
          }
          onRemove={() => {
            handleSelectorRemove(currentField.id);
            setInlineFieldId(null);
          }}
          previewValue={previewValues?.[currentField.id]}
          previewLoading={previewLoading}
          onRefreshPreview={onPreviewRefresh}
          onPreviewTargetChange={setActivePreviewFieldId}
          onSelectorFocus={(selector) => {
            if (currentField.multiple) {
              setPreviewSelectorAllInput(selector);
              setPreviewSelectorInput('');
            } else {
              setPreviewSelectorInput(selector);
              setPreviewSelectorAllInput('');
            }
          }}
          onClose={() => setInlineFieldId(null)}
        />
      );
    }

    if (fieldConfigDialogId) {
      const resolvedField = fieldMap.get(fieldConfigDialogId) || null;
      if (resolvedField) {
        dialogField = resolvedField;
        dialogSelector = selectors[fieldConfigDialogId] || { selector: '' };
      }
    }

    let fieldConfigDialogContent: React.ReactNode = null;

    if (dialogField && dialogSelector) {
      const currentField = dialogField;
      const currentSelector = dialogSelector;

      fieldConfigDialogContent = (
        <FieldConfigDialog
          open
          onClose={() => setFieldConfigDialogId(null)}
          field={currentField}
          selector={currentSelector}
          onUpdate={(updatedSelector) =>
            handleSelectorUpdate(currentField.id, updatedSelector)
          }
          onRemove={() => {
            handleSelectorRemove(currentField.id);
            setFieldConfigDialogId(null);
          }}
          previewValue={previewValues?.[currentField.id]}
          previewLoading={previewLoading}
          onRefreshPreview={onPreviewRefresh}
          onPreviewTargetChange={setActivePreviewFieldId}
          onSelectorFocus={(selector) => {
            if (currentField.multiple) {
              setPreviewSelectorAllInput(selector);
              setPreviewSelectorInput('');
            } else {
              setPreviewSelectorInput(selector);
              setPreviewSelectorAllInput('');
            }
          }}
        />
      );
    }

    return (
      <>
        <div>
          <Accordion
            expanded={previewAccordionExpanded}
            onChange={(_, expanded) => setPreviewAccordionExpanded(expanded)}
            className="mb-6"
          >
            <AccordionSummary expandIcon={<ChevronDown className="h-4 w-4" />}>
              <span className="text-base font-bold">
                Preview URL
              </span>
            </AccordionSummary>
            <AccordionDetails>
              <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-start">
                <Input
                  label="Preview URL"
                  value={previewUrlInput}
                  onChange={(event) =>
                    onPreviewUrlInputChange?.((event.target as HTMLInputElement).value)
                  }
                  placeholder="Enter a page URL to load sample HTML"
                  fullWidth
                />
                <div className="flex flex-col gap-2 shrink-0 w-full md:w-auto">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="primary"
                      onClick={() => onLoadPreview?.(previewUrlInput)}
                      disabled={
                        previewLoadLoading ||
                        !onLoadPreview ||
                        !previewUrlInput.trim()
                      }
                      className="whitespace-nowrap"
                    >
                      {previewLoadLoading && <Spinner size="sm" className="mr-2" />}
                      Load Data
                    </Button>
                    {onTestUrl && (
                      <Button
                        variant="outline"
                        onClick={onTestUrl}
                        disabled={!previewHtml}
                        className="whitespace-nowrap"
                      >
                        <Bug className="h-4 w-4 mr-2" />
                        Test URL
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Loading data fetches the raw HTML directly from the provided URL
                in your browser without calling the Country Homes API. After the
                data is loaded you can run API-powered actions like testing.
              </p>
              {previewLoadError && (
                <Alert variant="error" className="mt-4">
                  {previewLoadError}
                </Alert>
              )}
              {previewSourceUrl && (
                <Alert
                  variant={previewError ? 'warning' : 'info'}
                  className="mt-4"
                  onClose={onClearPreview}
                >
                  <p className="text-sm">
                    Preview loaded from{' '}
                    <a
                      href={previewSourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline"
                    >
                      {previewSourceUrl}
                    </a>
                  </p>
                  {previewError && (
                    <p className="text-sm text-gray-600 mt-2">
                      {previewError}
                    </p>
                  )}
                  {previewLoading && (
                    <div className="flex items-center gap-2 mt-2">
                      <Spinner size="sm" />
                      <p className="text-sm text-gray-600">
                        Generating preview with current selectors...
                      </p>
                    </div>
                  )}
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-4">
              <h2 className="text-lg font-semibold mb-4">
                Scraper Configuration
              </h2>

              <h3 className="text-base font-medium mb-2">
                Property Field Selectors & Transforms
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure the CSS selectors and transformations for each
                property field.
              </p>

              <div className="mb-8">
                <Select
                  label="Search and add field..."
                  value=""
                  onChange={(e) => {
                    const fieldId = (e.target as HTMLSelectElement).value;
                    const field = availableFields.find(f => f.id === fieldId);
                    if (field) {
                      handleFieldSelect(field);
                      // Reset the select after adding
                      (e.target as HTMLSelectElement).value = '';
                    }
                  }}
                  options={[
                    { value: '', label: 'Select a field to add...' },
                    ...availableFields.map(field => ({
                      value: field.id,
                      label: `${field.name} - ${field.description}`
                    }))
                  ]}
                  fullWidth
                />
              </div>

              {inlineFieldConfigContent ? (
                <div>{inlineFieldConfigContent}</div>
              ) : activeFields.length === 0 ? (
                <div className="p-6 text-center bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">
                    No fields configured yet. Use the search box above to add
                    property fields.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    {activeFields.length} field
                    {activeFields.length !== 1 ? 's' : ''} configured. Click a
                    field to focus it in the preview, double-click to open the
                    dialog, or use the gear icon to edit inline.
                  </p>
                  <div className="border border-gray-300 rounded overflow-hidden">
                    <div className="max-h-[420px] overflow-y-auto">
                      {activeFields.map((field, index) => {
                        const previewValue = previewValues?.[field.id];
                        const formatted = formatPreviewValue(previewValue, {
                          countOnly: field.multiple,
                        });
                        const hasPreview =
                          previewValue !== undefined &&
                          previewValue !== null &&
                          formatted !== '';

                        return (
                          <React.Fragment key={field.id}>
                            <div className="relative">
                              <button
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                                  activePreviewFieldId === field.id ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => {
                                  setActivePreviewFieldId(field.id);
                                }}
                                onDoubleClick={() => {
                                  setFieldConfigDialogId(field.id);
                                  setInlineFieldId(null);
                                }}
                              >
                                <div className="pr-20">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="text-base font-bold">
                                      {field.name}
                                    </span>
                                    {field.multiple && (
                                      <Badge variant="default" size="sm">
                                        Multiple
                                      </Badge>
                                    )}
                                    {field.complex && (
                                      <Badge variant="default" size="sm">
                                        Complex
                                      </Badge>
                                    )}
                                    {field.type === 'valueList' && (
                                      <Badge variant="info" size="sm">
                                        Value List
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                    {hasPreview
                                      ? formatted
                                      : 'No preview available yet for this field.'}
                                  </p>
                                </div>
                              </button>
                              <div className="absolute right-2 top-2 flex flex-col gap-1">
                                <button
                                  className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActivePreviewFieldId(field.id);
                                    setInlineFieldId(field.id);
                                    setFieldConfigDialogId(null);
                                  }}
                                  title="Configure field"
                                >
                                  <Settings className="h-4 w-4" />
                                </button>
                                <button
                                  className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleSelectorRemove(field.id);
                                  }}
                                  title="Remove field"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            {index < activeFields.length - 1 && (
                              <div className="border-b border-gray-300" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8">
                <h3 className="text-base font-medium mb-2">
                  JavaScript preprocessors
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Extract inline script data and expose it to field selectors.
                  The returned objects are merged and available via selectors
                  like <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">js.property.details</code> or{' '}
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">js.media.photos[0]</code>.
                </p>
                <Alert variant="info" className="mb-4">
                  Within each extractor you can use <code className="bg-blue-100 px-1 py-0.5 rounded text-xs">value</code> (the
                  selected script text) and <code className="bg-blue-100 px-1 py-0.5 rounded text-xs">$</code> (the cheerio
                  instance). Return an object to make nested values accessible
                  to the rest of your configuration.
                </Alert>
                {jsConfigs.length === 0 ? (
                  <div className="p-4 mb-4 bg-gray-50 border border-dashed border-gray-300 rounded">
                    <p className="text-sm text-gray-600">
                      No preprocessors configured yet. Use the button below to
                      add a selector/function pair for inline JSON blobs.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 mb-4">
                    {jsConfigs.map((config, index) => {
                      const extractorPreview = jsExtractorResults?.find(
                        (entry) => entry.index === index
                      );
                      const hasPreviewHtml = Boolean(previewHtml);
                      const matchesLabel = hasPreviewHtml
                        ? extractorPreview
                          ? extractorPreview.matches === 0
                            ? 'No matching script tags found for this selector.'
                            : `${extractorPreview.matches} match${
                                extractorPreview.matches === 1 ? '' : 'es'
                              } found.`
                          : 'Preview has not been generated for this extractor yet.'
                        : 'Load preview data to evaluate this extractor.';

                      let previewContent: React.ReactNode;
                      if (!hasPreviewHtml) {
                        previewContent = (
                          <p className="text-sm text-gray-600">
                            Load preview data to inspect the extracted object.
                          </p>
                        );
                      } else if (previewLoading) {
                        previewContent = (
                          <div className="flex items-center gap-2">
                            <Spinner size="sm" />
                            <p className="text-sm text-gray-600">
                              Refreshing preview...
                            </p>
                          </div>
                        );
                      } else if (!extractorPreview) {
                        previewContent = (
                          <p className="text-sm text-gray-600">
                            Preview not available yet. Refresh once this
                            extractor is configured.
                          </p>
                        );
                      } else if (extractorPreview.results.length > 0) {
                        const normalizedResult =
                          extractorPreview.results.length === 1
                            ? extractorPreview.results[0]
                            : extractorPreview.results;
                        let formatted = '';
                        if (typeof normalizedResult === 'string') {
                          formatted = normalizedResult;
                        } else {
                          try {
                            formatted = JSON.stringify(
                              normalizedResult,
                              null,
                              2
                            );
                          } catch (error) {
                            formatted = String(normalizedResult);
                          }
                        }
                        previewContent = (
                          <pre className="font-mono text-xs whitespace-pre-wrap break-words m-0">
                            {formatted}
                          </pre>
                        );
                      } else {
                        previewContent = (
                          <p className="text-sm text-gray-600">
                            {extractorPreview.matches > 0
                              ? 'Matched script tags but the extractor returned no data.'
                              : 'No script tags matched this selector yet.'}
                          </p>
                        );
                      }

                      return (
                        <Card key={`js-config-${index}`} className="p-4">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <h4 className="text-sm font-medium">
                              Extractor #{index + 1}
                            </h4>
                            <button
                              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                              onClick={() => handleJsConfigRemove(index)}
                              title="Remove extractor"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="mt-4">
                            <Input
                              label="Script selector"
                              value={config.selector || ''}
                              onChange={(e) =>
                                handleJsConfigChange(index, {
                                  selector: (e.target as HTMLInputElement).value,
                                })
                              }
                              placeholder="script[type='application/json']"
                              fullWidth
                            />
                          </div>
                          <div className="mt-4">
                            <Input
                              label="Extraction function"
                              value={config.function || ''}
                              onChange={(e) =>
                                handleJsConfigChange(index, {
                                  function: (e.target as HTMLTextAreaElement).value,
                                })
                              }
                              fullWidth
                              multiline
                              rows={4}
                              placeholder="const data = JSON.parse(value); return { property: data };"
                            />
                            <div className="mt-2 text-xs text-gray-600 space-y-2">
                              <p>Examples:</p>
                              <code className="block bg-gray-100 p-2 rounded font-mono">
                                {`const lat=value.match(/"lat":"([^"]+)"/);
const lng=value.match(/"lng":"([^"]+)"/);
return { lat: lat && lat[1], lng: lng && lng[1] };`}
                              </code>
                              <code className="block bg-gray-100 p-2 rounded font-mono">
                                {`const match = value.match(/property\\s*=\\s*(\\{[\\s\\S]*?\\});/);
const data = match ? JSON.parse(match[1]) : {};
return data;`}
                              </code>
                            </div>
                          </div>
                          <div className="mt-4">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <h5 className="text-sm font-medium text-gray-600">
                                Extractor preview
                              </h5>
                              {onPreviewRefresh && (
                                <button
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={onPreviewRefresh}
                                  disabled={previewLoading || !previewHtml}
                                  title="Refresh preview"
                                >
                                  {previewLoading ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 block mt-1">
                              {matchesLabel}
                            </p>
                            {hasPreviewHtml && extractorPreview?.error && (
                              <Alert variant="warning" className="mt-2">
                                {extractorPreview.error}
                              </Alert>
                            )}
                            <div className="mt-3 p-3 bg-gray-50 border border-gray-300 rounded">
                              {previewContent}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
                <Button variant="outline" onClick={handleAddJsConfig}>
                  Add JS extractor
                </Button>
              </div>
            </div>
            <div className="lg:col-span-8">
              <ScraperPreviewPanel
                previewHtml={previewHtml}
                previewSourceUrl={previewSourceUrl}
                previewLoading={previewLoading}
                previewSelectorInput={previewSelectorInput}
                previewSelectorAllInput={previewSelectorAllInput}
                onPreviewSelectorInputChange={setPreviewSelectorInput}
                onPreviewSelectorAllInputChange={setPreviewSelectorAllInput}
                previewSelectorError={previewSelectorError}
                previewSelectorAllError={previewSelectorAllError}
                onPreviewSelectorErrorChange={setPreviewSelectorError}
                onPreviewSelectorAllErrorChange={setPreviewSelectorAllError}
                onPreviewRefresh={onPreviewRefresh}
                activeFieldName={activePreviewField?.name || null}
                singlePlaceholder={previewSinglePlaceholder}
                multiPlaceholder={previewMultiPlaceholder}
                onAssignSuggestion={handleAssignSuggestionRequest}
              />
            </div>
          </div>
        </div>
        {fieldConfigDialogContent}
        <Dialog
          open={assignDialogOpen}
          onClose={handleAssignDialogClose}
          maxWidth="sm"
          title="Select field for this selector"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleAssignDialogClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAssignDialogConfirm}
                disabled={!assignFieldId}
              >
                Continue
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            {pendingAssignSelector && (
              <div>
                <h5 className="text-sm font-medium mb-1">
                  Selector to assign
                </h5>
                <code className="block font-mono text-sm break-words border border-gray-300 rounded px-1.5 py-1 bg-white">
                  {pendingAssignSelector}
                </code>
              </div>
            )}
            <Select
              label="Field"
              value={assignFieldId || ''}
              onChange={(e) => setAssignFieldId((e.target as HTMLSelectElement).value || null)}
              options={[
                { value: '', label: 'Select a field...' },
                ...selectableFields.map(option => ({
                  value: option.id,
                  label: `${option.name} - ${option.description}`
                }))
              ]}
              fullWidth
            />
          </div>
        </Dialog>
      </>
    );
  }
);

ScraperConfigFields.displayName = 'ScraperConfigFields';

export default ScraperConfigFields;
